<template>
  <div class="page-control">
    <button
      class="page-control__btn"
      :disabled="modelValue <= 1"
      :title="'First page'"
      @click="goTo(1)"
    >
      <v-icon icon="mdi-skip-previous" size="small" />
    </button>
    <button
      class="page-control__btn"
      :disabled="modelValue <= 1"
      :title="'Previous page'"
      @click="goTo(modelValue - 1)"
    >
      <v-icon icon="mdi-chevron-left" size="small" />
    </button>

    <button
      ref="pageDisplayBtn"
      class="page-control__page-display"
      :title="'Go to page'"
      @click="openPageDialog"
    >
      {{ modelValue }} <span class="">/{{ totalPages }}</span>
    </button>

    <button
      class="page-control__btn"
      :disabled="modelValue >= totalPages"
      :title="'Next page'"
      @click="goTo(modelValue + 1)"
    >
      <v-icon icon="mdi-chevron-right" size="small" />
    </button>
    <button
      class="page-control__btn"
      :disabled="modelValue >= totalPages"
      :title="'Last page'"
      @click="goTo(totalPages)"
    >
      <v-icon icon="mdi-skip-next" size="small" />
    </button>

    <!-- Go-to-page dialog -->
    <div
      v-if="showDialog"
      class="page-control__overlay"
      :style="{ top: dialogTop, left: dialogLeft }"
      @click.self="closeDialog"
    >
      <div class="page-control__dialog">
        <label class="page-control__dialog-label">Go to page (1–{{ totalPages }})</label>
        <input
          ref="pageInput"
          v-model.number="inputPage"
          type="number"
          class="page-control__dialog-input"
          :min="1"
          :max="totalPages"
          @keydown.enter="submitPage"
          @keydown.escape="closeDialog"
        >
        <span v-if="inputError" class="page-control__dialog-error">{{ inputError }}</span>
        <div class="page-control__dialog-actions">
          <button class="page-control__dialog-btn page-control__dialog-btn--cancel" @click="closeDialog">Cancel</button>
          <button class="page-control__dialog-btn page-control__dialog-btn--go" @click="submitPage">Go</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PageControl',
  props: {
    modelValue: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      showDialog: false,
      inputPage: 1,
      inputError: '',
      dialogTop: '0px',
      dialogLeft: '0px',
    };
  },
  methods: {
    goTo(page) {
      const p = Math.max(1, Math.min(page, this.totalPages));
      this.$emit('update:modelValue', p);
    },
    openPageDialog() {
      this.inputPage = this.modelValue;
      this.inputError = '';
      this.showDialog = true;
      // Position dialog below the button
      this.$nextTick(() => {
        if (this.$refs.pageDisplayBtn) {
          const rect = this.$refs.pageDisplayBtn.getBoundingClientRect();
          this.dialogTop = (rect.bottom + 8) + 'px';
          this.dialogLeft = (rect.left + rect.width / 2 - 110) + 'px'; // Center it horizontally relative to button
        }
        this.$refs.pageInput?.focus();
        this.$refs.pageInput?.select();
      });
    },
    closeDialog() {
      this.showDialog = false;
      this.inputError = '';
    },
    submitPage() {
      const p = parseInt(this.inputPage, 10);
      if (isNaN(p) || p < 1 || p > this.totalPages) {
        this.inputError = `Enter a number between 1 and ${this.totalPages}`;
        return;
      }
      this.$emit('update:modelValue', p);
      this.closeDialog();
    },
  },
};
</script>

<style lang="scss" scoped>
.page-control {
  display: flex;
  align-items: center;
  gap: 2px;
  position: relative;

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    background: transparent;
    font-size: 26px;
    color: #4a4a4a;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: rgba(10, 135, 181, 0.25);
      color: #0a87b5;
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  }

  &__page-display {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    min-width: 60px;
    padding: 0 10px;
    border: 1px solid;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    background: transparent;
    @include themify() {
      color: themed('text-color');
      border-color: themed('card-list-hover-outline');
    }

    &:hover {
      background: rgba(173, 198, 224, 0.25);
    }
  }

  &__of {
    font-weight: 400;
    opacity: 0.6;
    margin-left: 2px;
    font-size: 12px;
  }

  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    background: none;
  }

  &__dialog {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px 20px;
    border-radius: 8px;
    min-width: 220px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.95);
    background: #ffffff;
    color: #333333;
  }

  &__dialog-label {
    font-size: 13px;
    font-weight: 500;
  }

  &__dialog-input {
    width: 100%;
    height: 34px;
    padding: 0 8px;
    font-size: 14px;
    border: 1px solid;
    border-radius: 4px;
    outline: none;
    @include themify() {
      background: themed('image-details-background-fill');
      color: themed('text-color');
      border-color: themed('card-list-hover-outline');
    }

    &:focus {
      @include themify() {
        border-color: themed('primary-color');
      }
    }
  }

  &__dialog-error {
    font-size: 11px;
    color: #e53935;
  }

  &__dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
  }

  &__dialog-btn {
    padding: 5px 14px;
    font-size: 12px;
    font-weight: 500;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &--cancel {
      background: #e0e0e0;
      color: #333333;
      border: 1px solid #ccc;

      &:hover {
        background: #d0d0d0;
      }
    }

    &--go {
      background: #0087be;
      color: #ffffff;
      border: 1px solid #005f8c;

      &:hover {
        background: #006fa8;
      }
    }
  }
}
</style>
